import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import SearchBar from '../components/SearchBar'
import TimelineCard from '../components/TimelineCard'
import TimelineDetail from '../components/TimelineDetail'
import SEOHead, { seoConfigs } from '../components/SEOHead'
import aiTimelineData from '../data/aiTimeline.json'

const TimelinePage = () => {
  const [selectedTimeline, setSelectedTimeline] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isAutoScrolling, setIsAutoScrolling] = useState(true)
  const [isNavExpanded, setIsNavExpanded] = useState(() => {
    // Default to expanded on desktop (>= 640px), collapsed on mobile
    return typeof window !== 'undefined' ? window.innerWidth >= 640 : true
  })
  const timelineRef = useRef(null)

  // Auto-scroll through timeline items
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % aiTimelineData.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Scroll to specific timeline item
  const scrollToTimeline = (index) => {
    setActiveIndex(index)
    const element = timelineRef.current?.children[index]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Quick navigation years
  const quickNavYears = [
    { year: '1940s', index: 0, label: 'Foundation' },
    { year: '1960s', index: 1, label: 'Early AI' },
    { year: '1980s', index: 2, label: 'Neural Nets' },
    { year: '2000s', index: 3, label: 'Big Data' },
    { year: '2017', index: 4, label: 'Transformers' },
    { year: '2020', index: 5, label: 'Generative AI' },
    { year: '2023', index: 6, label: 'Mainstream' },
    { year: '2025', index: 7, label: 'Future' }
  ]

  return (
    <>
      <SEOHead {...seoConfigs.timeline} />
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center space-y-6">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            AI Evolution Timeline
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Journey through the fascinating evolution of artificial intelligence, from theoretical foundations to the cutting-edge technologies shaping our future.
          </p>
        
          {/* Animated Stats */}
          <div className="flex items-center justify-center space-x-8 text-white/60">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span>80+ Years of Innovation</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <span>{aiTimelineData.length} Major Eras</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
              <span>Countless Breakthroughs</span>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="glass-card rounded-2xl p-4 sm:p-6">
          <button
            onClick={() => setIsNavExpanded(!isNavExpanded)}
            className="w-full flex items-center justify-between text-white font-semibold mb-4 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-2 hover:bg-white/10 transition-all duration-300"
            aria-expanded={isNavExpanded}
            aria-controls="navigation-content"
          >
            <span className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3-3 3m-4-6l3 3-3 3" />
              </svg>
              <span>Quick Navigation</span>
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full">
                {quickNavYears[activeIndex]?.label}
              </span>
            </span>
            <svg 
              className={`w-5 h-5 transition-transform duration-300 ${isNavExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        
          <div 
            id="navigation-content"
            className={`transition-all duration-300 overflow-hidden ${
              isNavExpanded 
                ? 'max-h-96 opacity-100' 
                : 'max-h-0 opacity-0'
            }`}
          >
            <nav aria-label="Timeline era navigation">
              <div className="max-h-64 sm:max-h-none overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 pr-2">
                  {quickNavYears.map((nav, index) => (
                    <button
                      key={nav.year}
                      onClick={() => {
                        scrollToTimeline(nav.index)
                        // Auto-collapse on mobile after selection
                        if (window.innerWidth < 640) {
                          setIsNavExpanded(false)
                        }
                      }}
                      className={`p-2 sm:p-3 rounded-lg text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                        activeIndex === nav.index
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                          : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white hover:scale-105'
                      }`}
                      aria-label={`Navigate to ${nav.label} era (${nav.year})`}
                      aria-pressed={activeIndex === nav.index}
                      type="button"
                    >
                      <div className="text-xs opacity-75 mb-1">{nav.label}</div>
                      <div className="font-mono text-xs sm:text-sm">{nav.year}</div>
                    </button>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        </div>

        {/* Current Era Highlight */}
        <div className="glass-card rounded-2xl p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
            <h3 className="text-white font-semibold">Currently Viewing</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-2xl font-bold text-white mb-2">
                {aiTimelineData[activeIndex]?.title}
              </h4>
              <p className="text-white/80 mb-3">{aiTimelineData[activeIndex]?.subtitle}</p>
              <p className="text-white/70 text-sm">{aiTimelineData[activeIndex]?.description}</p>
            </div>
            <div className="flex items-center justify-center">
              <div className={`w-24 h-24 bg-gradient-to-br ${aiTimelineData[activeIndex]?.color} rounded-full flex items-center justify-center animate-pulse`}>
                <span className="text-white font-bold text-lg">{aiTimelineData[activeIndex]?.year.split('-')[0]}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Grid */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Explore Each Era
          </h2>
          <div 
            ref={timelineRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            role="grid"
            aria-label="AI timeline eras"
          >
            {aiTimelineData.map((timelineItem, index) => (
              <div 
                key={timelineItem.id}
                style={{ animationDelay: `${index * 0.1}s` }}
                className="animate-float"
              >
                <TimelineCard
                  timelineItem={timelineItem}
                  isActive={activeIndex === index}
                  onClick={() => {
                    setActiveIndex(index)
                    setSelectedTimeline(timelineItem)
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Interactive Timeline Visualization */}
        <section className="glass-card rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Interactive Timeline
          </h2>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 rounded-full" aria-hidden="true"></div>
            
            {/* Timeline Points */}
            <div className="space-y-8">
              {aiTimelineData.map((item, index) => (
                <div 
                  key={item.id}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} group cursor-pointer`}
                  onClick={() => {
                    setActiveIndex(index)
                    setSelectedTimeline(item)
                  }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className={`glass-card rounded-xl p-4 hover:scale-105 transition-all duration-300 ${
                      activeIndex === index ? 'ring-2 ring-white/30' : ''
                    }`}>
                      <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                      <p className="text-white/70 text-sm mb-2">{item.year}</p>
                      <p className="text-white/60 text-xs">{item.subtitle}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Point */}
                  <button
                    onClick={() => {
                      setActiveIndex(index)
                      setSelectedTimeline(item)
                    }}
                    className={`w-6 h-6 bg-gradient-to-r ${item.color} rounded-full border-4 border-white/20 z-10 hover:scale-125 transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                      activeIndex === index ? 'scale-125 animate-pulse' : ''
                    }`}
                    aria-label={`View details for ${item.title} (${item.year})`}
                    type="button"
                  ></button>
                  
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Detail Modal */}
        {selectedTimeline && (
          <TimelineDetail
            timelineItem={selectedTimeline}
            onClose={() => {
              console.log('Modal close requested') // Debug log
              setSelectedTimeline(null)
            }}
          />
        )}
      </div>
    </>
  )
}

export default TimelinePage
